import { Lock } from 'lucide-react';
import { useCurrentUser, useLicence } from '@/services/queries';
import { StatusPill } from '@/shared/ui/status-pill';
import { Skeleton } from '@/shared/ui/skeleton';
import type { CurrentUser } from '@/types';

/**
 * Who the signed-in person is, as the design's Profile card draws it.
 *
 * Three bands: a header (48px initials tile, name, role), a label/value list of
 * the person's details from `/auth/me`, and "Seat & device" from their own seat
 * on `/licence`. Label column 140px, rows capped at 420px - transcribed.
 *
 * "Last login" is the last password sign-in with this email at the customer
 * portal or the desktop app, as the server records it; token refreshes do not
 * move it. Everything here is read-only, as the footer says.
 */

const ROLE_LABELS: Record<CurrentUser['role'], string> = {
  owner: 'Owner',
  technician: 'Technician',
  viewer: 'Viewer',
};

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

/** "22 Aug 13:58 UTC", as the design writes it. Shown in UTC so two people in
 *  different offices reading the same account see the same time. */
function formatLastLogin(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const date = new Date(iso);
  const day = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${day} ${time} UTC`;
}

/** One label/value row, as the design's `profileFields` loop draws it. */
function Row({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid max-w-[420px] grid-cols-[140px_1fr] items-center gap-3">
      <span className="text-[11.5px] text-[#8A94A6]">{label}</span>
      <span
        className={
          mono
            ? 'font-mono text-[11.5px] font-medium [overflow-wrap:anywhere]'
            : 'text-xs font-medium [overflow-wrap:anywhere]'
        }
      >
        {children}
      </span>
    </div>
  );
}

export function ProfileCard() {
  const { data: user, isPending } = useCurrentUser();
  const licenceQuery = useLicence();

  if (isPending) return <Skeleton className="h-96 w-full rounded-2xl" />;
  if (!user) return null;

  const seat = licenceQuery.data?.licence?.seats.find((s) => s.isYou);
  const role = ROLE_LABELS[user.role];

  return (
    <div className="min-w-0 overflow-hidden rounded-2xl border border-[#E3E7ED] bg-card shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
      {/* Header. */}
      <div className="flex items-center gap-4 border-b border-[#F1F4F8] p-[26px]">
        <span
          className="flex size-12 flex-none items-center justify-center rounded-[13px] bg-primary text-base font-semibold text-primary-foreground"
          aria-hidden="true"
        >
          {initialsOf(user.name)}
        </span>
        <div className="min-w-0">
          <div className="text-[15.5px] font-semibold tracking-[-0.2px]">{user.name}</div>
          <div className="mt-0.5 text-[11.5px] text-[#8A94A6]">{role}</div>
        </div>
      </div>

      {/* Details. */}
      <div className="flex flex-col gap-4 border-b border-[#F1F4F8] px-[26px] py-[22px]">
        <Row label="Full name">{user.name}</Row>
        <Row label="Username">{user.username ?? '—'}</Row>
        <Row label="Email">{user.email}</Row>
        <Row label="Role">{role}</Row>
      </div>

      {/* Seat & device. */}
      <div className="flex flex-col gap-3.5 border-b border-[#F1F4F8] px-[26px] py-[22px]">
        <div className="text-xs font-semibold">Seat &amp; device</div>
        {seat ? (
          <>
            <Row label="Seat" mono>
              Seat {String(seat.number)} ·{' '}
              {seat.deviceLabel ? `WKS-${seat.deviceLabel.slice(0, 4)}` : 'not bound'}
            </Row>
            <Row label="Status">
              <StatusPill status={seat.status} />
            </Row>
            <Row label="Fingerprint">
              <StatusPill status={seat.fingerprint} />
            </Row>
          </>
        ) : (
          <Row label="Seat">
            <span className="text-[#8A94A6]">No seat assigned to you</span>
          </Row>
        )}
        <Row label="Last login" mono>
          {formatLastLogin(user.lastLoginAt)}
        </Row>
      </div>

      {/* The design's read-only note. */}
      <div className="flex items-start gap-2.5 bg-[#F7F9FC] px-[26px] py-[18px]">
        <Lock
          className="mt-px size-3.5 flex-none text-[#8A94A6]"
          strokeWidth={1.9}
          aria-hidden="true"
        />
        <p className="text-[11px] leading-[1.6] text-[#6B7688]">
          Read-only for everyone, including the owner. Seat reassignment and revocation are handled
          by NuDent support, not in this portal.
        </p>
      </div>
    </div>
  );
}
